import React, { useEffect, useMemo, useRef, useState } from 'react';

import { styled } from 'styled-components';

import { MenuItem, Select, SelectChangeEvent, Tooltip } from '@mui/material';
import { Input } from 'components';
import { CloseCircle } from 'iconsax-react';
import FilterBetween from 'images/filter-between.svg';
import FilterEquals from 'images/filter-equals.svg';
import FilterGreaterThan from 'images/filter-grater-than.svg';
import FilterGreaterThanOrEqual from 'images/filter-greater-than-or-equal.svg';
import FilterLessThanOrEqual from 'images/filter-less-than-or-equal.svg';
import FilterLessThan from 'images/filter-less-than.svg';
import FilterNoEquals from 'images/filter-no-equals.svg';
import { useTranslation } from 'react-i18next';

import { ColumnFilteringProps } from '../types';

import { _ } from 'third-party';

import { USER_INPUT_DEBOUNCE_TIME_MS } from 'constants/common';

import { APIDecimalSearchFilter, APINumericOperators } from 'types/api';

const RESET_FILTER = '__reset__';

const FilterWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0.25rem;
  padding: 0.1rem 0 0.3rem 0;

  & input {
    padding: 0.1rem !important;
  }
`;

const ConditionSelect = styled(Select)`
  min-width: 2.5rem;
  max-width: 4rem;
  font-size: 0.75rem;

  & .MuiSelect-select {
    display: flex;
    align-items: center;
    padding: 0.1rem 0.25rem !important;
  }
`;

const FilterIcon = styled.img`
  width: 16px;
  height: 16px;
  filter: invert(32%) sepia(95%) saturate(1200%) hue-rotate(162deg)
    brightness(88%) contrast(101%);
`;

const RangeSeparator = styled.span`
  align-self: center;
  font-size: 0.75rem;
`;

type OperatorConfig = {
  icon: string | null;
  label: string;
  i18nKey: string;
};

const operatorConfig: Record<APINumericOperators, OperatorConfig> = {
  [APINumericOperators.Equals]: {
    icon: FilterEquals,
    label: '=',
    i18nKey: 'filter.numeric.equals',
  },
  [APINumericOperators.NotEquals]: {
    icon: FilterNoEquals,
    label: '≠',
    i18nKey: 'filter.numeric.notEquals',
  },
  [APINumericOperators.GreaterThan]: {
    icon: FilterGreaterThan,
    label: '>',
    i18nKey: 'filter.numeric.greaterThan',
  },
  [APINumericOperators.GreaterThanOrEqual]: {
    icon: FilterGreaterThanOrEqual,
    label: '≥',
    i18nKey: 'filter.numeric.greaterThanOrEqual',
  },
  [APINumericOperators.LessThan]: {
    icon: FilterLessThan,
    label: '<',
    i18nKey: 'filter.numeric.lessThan',
  },
  [APINumericOperators.LessThanOrEqual]: {
    icon: FilterLessThanOrEqual,
    label: '≤',
    i18nKey: 'filter.numeric.lessThanOrEqual',
  },
  [APINumericOperators.Between]: {
    icon: FilterBetween,
    label: '⟷',
    i18nKey: 'filter.numeric.between',
  },
};

export const AdvancedNumericFilter: React.FC<ColumnFilteringProps> = ({
  columnId,
  filterValue,
  onAdvancedFilterChange,
}) => {
  const { t } = useTranslation();
  const filterRef = useRef<HTMLInputElement>(null);
  const filterToRef = useRef<HTMLInputElement>(null);

  const currentFilter = filterValue as APIDecimalSearchFilter | undefined;

  const [condition, setCondition] = useState<APINumericOperators>(
    currentFilter?.condition || APINumericOperators.Equals,
  );
  const [value, setValue] = useState<string>(
    currentFilter?.value?.toString() || '',
  );
  const [valueTo, setValueTo] = useState<string>(
    currentFilter?.valueTo?.toString() || '',
  );

  const debouncedEmitChange = useMemo(
    () =>
      _.debounce(
        (
          newCondition: APINumericOperators,
          newValue: string,
          newValueTo: string,
        ) => {
          const numValue = newValue ? parseFloat(newValue) : undefined;
          const numValueTo = newValueTo ? parseFloat(newValueTo) : undefined;

          const filter: APIDecimalSearchFilter = {
            condition: newCondition,
            value: numValue,
            valueTo:
              newCondition === APINumericOperators.Between
                ? numValueTo
                : undefined,
          };
          onAdvancedFilterChange?.(columnId.toString(), filter);
        },
        USER_INPUT_DEBOUNCE_TIME_MS,
      ),
    [columnId, onAdvancedFilterChange],
  );

  const handleConditionChange = (event: SelectChangeEvent<unknown>) => {
    const selectedValue = event.target.value as string;

    if (selectedValue === RESET_FILTER) {
      setCondition(APINumericOperators.Equals);
      setValue('');
      setValueTo('');
      if (filterRef.current) {
        filterRef.current.value = '';
      }
      if (filterToRef.current) {
        filterToRef.current.value = '';
      }
      debouncedEmitChange.cancel();
      onAdvancedFilterChange?.(columnId.toString(), undefined);
      return;
    }

    const newCondition = selectedValue as APINumericOperators;
    setCondition(newCondition);
    debouncedEmitChange(newCondition, value, valueTo);
  };

  const handleValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setValue(newValue);
    debouncedEmitChange(condition, newValue, valueTo);
  };

  const handleValueToChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValueTo = event.target.value;
    setValueTo(newValueTo);
    debouncedEmitChange(condition, value, newValueTo);
  };

  useEffect(() => {
    if (currentFilter) {
      setCondition(currentFilter.condition || APINumericOperators.Equals);
      setValue(currentFilter.value?.toString() || '');
      setValueTo(currentFilter.valueTo?.toString() || '');
      if (filterRef.current) {
        filterRef.current.value = currentFilter.value?.toString() || '';
      }
      if (filterToRef.current) {
        filterToRef.current.value = currentFilter.valueTo?.toString() || '';
      }
    } else {
      setCondition(APINumericOperators.Equals);
      setValue('');
      setValueTo('');
      if (filterRef.current) {
        filterRef.current.value = '';
      }
      if (filterToRef.current) {
        filterToRef.current.value = '';
      }
    }
  }, [currentFilter]);

  const showRangeInput = condition === APINumericOperators.Between;

  return (
    <FilterWrapper>
      <ConditionSelect
        size="small"
        variant="standard"
        value={condition}
        onChange={handleConditionChange}
        sx={{
          '& legend': { display: 'none' },
          '& fieldset': { top: 0 },
        }}
      >
        <MenuItem value={RESET_FILTER} sx={{ fontSize: '0.875rem' }}>
          <Tooltip title={t('filter.clear' as any)} placement="right">
            <span style={{ display: 'flex', alignItems: 'center' }}>
              <CloseCircle variant="TwoTone" size="16" color={'#007170'} />
            </span>
          </Tooltip>
        </MenuItem>
        {Object.entries(operatorConfig).map(([op, config]) => (
          <MenuItem key={op} value={op} sx={{ fontSize: '0.875rem' }}>
            <Tooltip title={t(config.i18nKey as any)} placement="right">
              <span style={{ display: 'flex', alignItems: 'center' }}>
                {config.icon ? (
                  <FilterIcon src={config.icon} alt={config.label} />
                ) : (
                  config.label
                )}
              </span>
            </Tooltip>
          </MenuItem>
        ))}
      </ConditionSelect>
      <Input
        inputRef={filterRef}
        type="number"
        defaultValue={value}
        onChange={handleValueChange}
        sx={{ flexGrow: 1, maxWidth: showRangeInput ? '3rem' : undefined }}
      />
      {showRangeInput && (
        <>
          <RangeSeparator>-</RangeSeparator>
          <Input
            inputRef={filterToRef}
            type="number"
            defaultValue={valueTo}
            onChange={handleValueToChange}
            sx={{ flexGrow: 1, maxWidth: '3rem' }}
          />
        </>
      )}
    </FilterWrapper>
  );
};
